import { useEffect, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import {
  getAllSalespeopleAdmin,
  createSalesperson,
  updateSalesperson,
  deleteSalesperson,
} from "../apiHelper";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import ResponsiveTable from "../components/ResponsiveTable";
import {
  UserCog,
  Plus,
  Edit,
  Trash2,
  Mail,
  Shield,
  Calendar,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import SalespersonModal from "../components/SalespersonModal";

const Salespeople = () => {
  const [salespeople, setSalespeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSalesperson, setEditingSalesperson] = useState(null);
  const { getAccessTokenSilently } = useAuth();

  useEffect(() => {
    fetchSalespeople();
  }, []);

  const fetchSalespeople = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const result = await getAllSalespeopleAdmin(token);

      if (result.success) {
        setSalespeople(result.data || []);
      } else {
        throw new Error(result.message || "Failed to load salespeople");
      }
    } catch (err) {
      setError(err.message || "Failed to load salespeople");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSalesperson = () => {
    setEditingSalesperson(null);
    setShowModal(true);
  };

  const handleEditSalesperson = (salesperson) => {
    setEditingSalesperson(salesperson);
    setShowModal(true);
  };

  const handleSaveSalesperson = async (salespersonData) => {
    try {
      setMessage(null);
      const token = await getAccessTokenSilently();

      let result;
      if (editingSalesperson) {
        // Update existing salesperson
        result = await updateSalesperson(
          token,
          editingSalesperson._id,
          salespersonData
        );
      } else {
        // Create new salesperson
        result = await createSalesperson(token, salespersonData);
      }

      if (result.success) {
        setMessage({
          type: "success",
          text: editingSalesperson
            ? "Salesperson updated successfully!"
            : "Salesperson created successfully!",
        });
        setShowModal(false);
        setEditingSalesperson(null);
        fetchSalespeople();
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    }
  };

  const handleDeleteSalesperson = async (salespersonId) => {
    if (
      !window.confirm(
        "Are you sure you want to deactivate this salesperson? They will no longer be able to log in."
      )
    ) {
      return;
    }

    try {
      setMessage(null);
      const token = await getAccessTokenSilently();
      const result = await deleteSalesperson(token, salespersonId);

      if (result.success) {
        setMessage({
          type: "success",
          text: "Salesperson deactivated successfully!",
        });
        fetchSalespeople();
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-6">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  const getRoleBadge = (role) => {
    const roleConfig = {
      super_admin: {
        label: "Super Admin",
        className: "!bg-purple-100 !text-purple-800 !border-purple-200",
      },
      admin: {
        label: "Admin",
        className: "!bg-blue-100 !text-blue-800 !border-blue-200",
      },
      salesperson: {
        label: "Salesperson",
        className: "!bg-green-100 !text-green-800 !border-green-200",
      },
    };
    const config = roleConfig[role] || roleConfig.salesperson;
    return (
      <Badge
        variant="outline"
        className={`${config.className} border font-medium`}
      >
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge
        variant="outline"
        className="!bg-green-100 !text-green-800 !border-green-200 border font-medium"
      >
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Active
      </Badge>
    ) : (
      <Badge
        variant="outline"
        className="!bg-gray-100 !text-gray-800 !border-gray-200 border font-medium"
      >
        <XCircle className="h-3 w-3 mr-1" />
        Inactive
      </Badge>
    );
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      render: (salesperson) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-white font-semibold text-sm">
              {salesperson.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2) || "??"}
            </span>
          </div>
          <div>
            <div className="font-semibold text-gray-900">
              {salesperson.name}
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <Mail className="h-3 w-3" />
              {salesperson.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      label: "Role",
      render: (salesperson) => (
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-gray-500" />
          {getRoleBadge(salesperson.role)}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (salesperson) => getStatusBadge(salesperson.isActive),
    },
    {
      key: "lastLogin",
      label: "Last Login",
      render: (salesperson) => (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4" />
          {salesperson.lastLogin
            ? new Date(salesperson.lastLogin).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "Never"}
        </div>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (salesperson) => (
        <div className="text-sm text-gray-600">
          {new Date(salesperson.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (salesperson) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEditSalesperson(salesperson)}
            className="!border-gray-300 !text-gray-700 hover:!bg-gray-50"
          >
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </Button>
          {salesperson.isActive && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDeleteSalesperson(salesperson._id)}
              className="!border-red-300 !text-red-700 hover:!bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Deactivate
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Stats calculation
  const stats = {
    total: salespeople.length,
    active: salespeople.filter((s) => s.isActive).length,
    inactive: salespeople.filter((s) => !s.isActive).length,
    admins: salespeople.filter(
      (s) => s.role === "admin" || s.role === "super_admin"
    ).length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <UserCog className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                Salespeople Management
              </h1>
              <p className="text-sm lg:text-base text-gray-600">
                Manage salesperson accounts and permissions
              </p>
            </div>
          </div>
          <Button
            onClick={handleAddSalesperson}
            className="!bg-[#00C68D] hover:!bg-[#01C891] !text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Salesperson
          </Button>
        </div>

        {/* Message */}
        {message && (
          <Alert
            severity={message.type === "success" ? "success" : "error"}
            className="rounded-xl"
          >
            {message.text}
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-gray-600">
                    Total
                  </p>
                  <p className="text-2xl lg:text-3xl font-bold text-gray-900 mt-1">
                    {stats.total}
                  </p>
                </div>
                <UserCog className="h-8 w-8 lg:h-10 lg:w-10 text-indigo-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-gray-600">
                    Active
                  </p>
                  <p className="text-2xl lg:text-3xl font-bold text-green-600 mt-1">
                    {stats.active}
                  </p>
                </div>
                <CheckCircle2 className="h-8 w-8 lg:h-10 lg:w-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-gray-600">
                    Inactive
                  </p>
                  <p className="text-2xl lg:text-3xl font-bold text-gray-600 mt-1">
                    {stats.inactive}
                  </p>
                </div>
                <XCircle className="h-8 w-8 lg:h-10 lg:w-10 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-gray-600">
                    Admins
                  </p>
                  <p className="text-2xl lg:text-3xl font-bold text-blue-600 mt-1">
                    {stats.admins}
                  </p>
                </div>
                <Shield className="h-8 w-8 lg:h-10 lg:w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Salespeople Table */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-gray-100 bg-white/50 backdrop-blur-sm">
            <CardTitle className="text-lg lg:text-xl text-gray-900">
              All Salespeople
            </CardTitle>
            <CardDescription className="text-sm lg:text-base">
              View and manage all salesperson accounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {salespeople.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <UserCog className="h-16 w-16 text-gray-400 mb-6" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-3">
                  No salespeople found
                </h3>
                <p className="text-base text-gray-500 mb-8 max-w-md text-center">
                  Create your first salesperson account to get started.
                </p>
                <Button
                  onClick={handleAddSalesperson}
                  className="!bg-[#00C68D] hover:!bg-[#01C891] !text-white px-6 py-3 text-base"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add First Salesperson
                </Button>
              </div>
            ) : (
              <ResponsiveTable
                columns={columns}
                data={salespeople}
                loading={false}
                emptyMessage="No salespeople available."
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Salesperson Modal */}
      {showModal && (
        <SalespersonModal
          salesperson={editingSalesperson}
          onClose={() => {
            setShowModal(false);
            setEditingSalesperson(null);
          }}
          onSave={handleSaveSalesperson}
        />
      )}
    </div>
  );
};

export default Salespeople;
